import { Document, ViewType } from '@consigli/types';
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export enum ViewerMode {
  Closed,
  CommentPreview,
  DocumentFullscreen,
}

type ViewerContextTypes = {
  document: Document | null;
  setDocument: Dispatch<SetStateAction<Document | null>>;
  mode: ViewerMode;
  setMode: Dispatch<SetStateAction<ViewerMode>>;
  availableDocuments: Document[];
  setAvailableDocuments: Dispatch<SetStateAction<Document[]>>;
  viewType: ViewType;
  setViewType: Dispatch<SetStateAction<ViewType>>;
  hasFullscreenHost: boolean;
  registerFullscreenHost: () => () => void;
  isStandaloneDocument: boolean;
  setIsStandaloneDocument: Dispatch<SetStateAction<boolean>>;
};

const ViewerContext = createContext<ViewerContextTypes | null>(null);

export const useViewerContext = () => {
  const ctx = useContext(ViewerContext);
  if (ctx === null) {
    throw new Error(`Called useViewerContext without ViewerProvider in tree!`);
  }
  return ctx;
};

export const ViewerProvider: FC<PropsWithChildren> = ({ children }) => {
  const [document, setDocument] = useState<Document | null>(null);
  const [mode, setMode] = useState<ViewerMode>(ViewerMode.Closed);
  const [availableDocuments, setAvailableDocuments] = useState<Document[]>([]);
  const [viewType, setViewType] = useState<ViewType>(ViewType.PORTRAIT);
  const [fullscreenHostCount, setFullscreenHostCount] = useState(0);
  const [isStandaloneDocument, setIsStandaloneDocument] = useState(false);

  const registerFullscreenHost = useCallback(() => {
    setFullscreenHostCount((count) => count + 1);
    return () => setFullscreenHostCount((count) => count - 1);
  }, []);

  return (
    <ViewerContext.Provider
      value={{
        document,
        setDocument,
        mode,
        setMode,
        availableDocuments,
        setAvailableDocuments,
        viewType,
        setViewType,
        hasFullscreenHost: fullscreenHostCount > 0,
        registerFullscreenHost,
        isStandaloneDocument,
        setIsStandaloneDocument,
      }}
    >
      {children}
    </ViewerContext.Provider>
  );
};

/**
 * Declares that this screen renders its own fullscreen viewer for
 * `ViewerMode.DocumentFullscreen`.
 *
 * The mode is global, so anything watching it renders at the same time. Agent
 * Chat needs a viewer of its own for screens that have none — a source citation
 * has to open somewhere — but without this it also rendered a second one on top
 * of the screens that already had a viewer. Registering lets the chat hold back
 * wherever a real host exists.
 */
export const useFullscreenViewerHost = (isHosting = true) => {
  const { registerFullscreenHost } = useViewerContext();

  useEffect(() => {
    // Screens that only host under extra conditions pass those in, so the chat
    // is not held back while the screen is in a state where it renders nothing.
    if (!isHosting) return;

    return registerFullscreenHost();
  }, [isHosting, registerFullscreenHost]);
};
